/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../util/IconList',
	'underscore'
], (StringResources, APIFactory, PropertiesProviderAPI, IconList, _) => {
	class PageProperties {
		constructor(options) {
			this.dashboard = options.dashboardAPI;
			this.content = options.content;
			this.content.getFeature('Properties').registerProvider(this.getAPI());
			this._icons = options.features['Dashboard.Icons'];
		}

		getAPI() {
			return APIFactory.createAPI(this, [PropertiesProviderAPI]);
		}

		_isTopLevelPage() {
			return !this.content.getContainer();
		}

		getPropertyList() {
			if(this._isTopLevelPage()) {
				return [];
			}
			const restoreMasterStyling = () => {
				const transaction = this.dashboard.getFeature('Transaction');
				const transactionToken = transaction.startTransaction();
				this.content.setPropertyValue('tabTextColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabSelectedLineColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabBackgroundColor', 'transparent', transactionToken);
				this.content.setPropertyValue('tabIconColor', 'transparent', transactionToken);
				transaction.endTransaction(transactionToken);
				this.dashboard.triggerDashboardEvent('properties:refreshChild', {
					content: this.content,
					focusSelector: '.clickCallback_left_0_tabRestoreDefaults'
				});
			};
			const translationService = this.dashboard.getFeature('TranslationService');
			return [
				{
					id: 'title',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('title');
					},
					setPropertyValue: (value) => {
						const layoutModel = this.getLayoutModel();
						if(layoutModel) {
							layoutModel.set({ title: value }, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: 'page.titleSection',
						uiControl: {
							type: 'InputLabel',
							label: StringResources.get('propTabTitle'),
							ariaLabel: StringResources.get('propTabTitle'),
							handleReturnKey: true,
							appendTranslationIcon: translationService.appendTranslationIcon.bind(translationService)
						}
					}
				}, {
					id: 'tabTextColor',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('tabTextColor');
					},
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor('tabTextColor', value, transactionToken);
					},
					editor: {
						sectionId: 'page.titleSection',
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get('propTabTextColor'),
							ariaLabel: StringResources.get('propTabTextColor'),
							paletteType: 'DashboardColorSet',
							open: false,
							showHexValue: true,
							addButton: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this.content.setPropertyValue(propertyName, propertyValueInfo.name);
							}
						}
					}
				}, {
					id: 'tabIcon',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						const icon = layoutModel.get('tabIcon');
						if(icon && icon !== 'transparent') {
							return this._icons.getIcon(icon).id;
						} else {
							return 'transparent';
						}
					},
					setPropertyValue: (value) => {
						const layoutModel = this.getLayoutModel();
						if(layoutModel) {
							// Transform string to object if input is string
							const iconList = IconList.getTabIcons(this._icons);
							if (typeof value === 'string') {
								value = _.find(iconList, (icon) => {
									return icon.value === value;
								});
								value = value || iconList[0]; // the first element is the transparent option
							}
							layoutModel.set({ tabIcon: value.name }, {
								payloadData: { skipUndoRedo: true }
							});
						}
					},
					editor: {
						sectionId: 'page.tabIconArea',
						uiControl: {
							type: 'CollapsiblePicker',
							label: StringResources.get('propTabIcon'),
							ariaLabel: StringResources.get('propTabIcon'),
							selectedName: this.getLayoutModel().get('tabIcon'),
							placeholder: {
								name: 'transparent',
								label: StringResources.get('no_icon'),
								type: 'string',
								value: 'transparent'
							},
							contentSize: 'small',
							items: IconList.getTabIcons(this._icons)
						}
					}
				}, {
					id: 'tabIconColor',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('tabIconColor');
					},
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor('tabIconColor', value, transactionToken);
					},
					editor: {
						sectionId: 'page.tabIconArea',
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get('propTabIconColor'),
							ariaLabel: StringResources.get('propTabIconColor'),
							paletteType: 'DashboardColorSet',
							open: false,
							showHexValue: true,
							addButton: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this.content.setPropertyValue(propertyName, propertyValueInfo.name);
							}
						}
					}
				}, {
					id: 'tabSelectedLineColor',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('tabSelectedLineColor');
					},
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor('tabSelectedLineColor', value, transactionToken);
					},
					editor: {
						sectionId: 'page.titleBackground',
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get('propTabSelectedBarColor'),
							ariaLabel: StringResources.get('propTabSelectedBarColor'),
							paletteType: 'DashboardColorSet',
							open: false,
							showHexValue: true,
							addButton: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this.content.setPropertyValue(propertyName, propertyValueInfo.name);
							}
						}
					}
				}, {
					id: 'tabBackgroundColor',
					getPropertyValue: () => {
						const layoutModel = this.getLayoutModel();
						return layoutModel.get('tabBackgroundColor');
					},
					setPropertyValue: (value, transactionToken) => {
						this._changeTabColor('tabBackgroundColor', value, transactionToken);
					},
					editor: {
						sectionId: 'page.titleBackground',
						uiControl: {
							type: 'ColorPicker',
							label: StringResources.get('propTabBackgroundColor'),
							ariaLabel: StringResources.get('propTabBackgroundColor'),
							paletteType: 'DashboardColorSet',
							open: false,
							showHexValue: true,
							addButton: true,
							onChange: (propertyName, propertyValueInfo = {}) => {
								this.content.setPropertyValue(propertyName, propertyValueInfo.name);
							}
						}
					}
				}, {
					id: 'tabRestoreDefaults',
					editor: {
						sectionId: 'page.titleBackground',
						uiControl: {
							type: 'SingleLineLinks',
							items: [{
								align: 'left',
								items: [{
									type: 'icon',
									class: 'iconTextLink',
									svgIcon: this._icons.getIcon('restore').id,
									iconTooltip: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}, {
								align: 'right',
								items: [{
									type: 'text',
									class: 'iconTextLink',
									value: StringResources.get('propTabRestoreDefaults'),
									ariaLabel: StringResources.get('propTabRestoreDefaults'),
									clickCallback: restoreMasterStyling
								}]
							}]
						}
					}
				}
			];
		}

		getPropertyLayoutList() {
			if(this._isTopLevelPage()) {
				return [];
			}
			return [{
				value: StringResources.get('individualTabProperties'),
				centerLabel: true,
				id: 'banner',
				type: 'Banner',
				backButton: true,
				ariaLabel: StringResources.get('individualTabProperties')
			}, {
				id: 'page',
				type: 'Group'
			}, {
				id: 'titleSection',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('sectionName_tabTitle')
			}, {
				id: 'titleBackground',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('sectionName_tabBackground')
			}, {
				id: 'tabIconArea',
				type: 'Section',
				collapsible: false,
				label: StringResources.get('propTabIconLabel')
			}];
		}

		getLayoutModel() {
			const boardModel = this.dashboard.getFeature('internal').getBoardModel();
			return boardModel.layout.findModel(this.content.getId());
		}

		_changeTabColor(propertyName, propertyValue) {
			// get layout and update CSS

			const payload = {};
			// Treat transparent as reset to default
			if(propertyValue) {
				payload[propertyName] = propertyValue == 'transparent' ? null : this.dashboard.getFeature('Colors').getColorClassName(propertyValue);
			} else {
				payload[propertyName] = null;
			}
			this.dashboard.getFeature('Colors').prepareForColorModelChange(payload, propertyName);

			const model = this.getLayoutModel();
			model.set(payload, {
				sender: this.senderId,
				payloadData: { skipUndoRedo: true }
			});
		}

	}
	return PageProperties;
});
